import React, { useEffect, useState } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Loader2, CheckCircle, AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function ReconciliationPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({ reconciliation_date: new Date().toISOString().split('T')[0], bank_balance: '' });

  const load = async () => { try { setRecords(await superAdminAPI.getReconciliations()); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const handleRun = async (e) => {
    e.preventDefault(); setRunning(true);
    try { await superAdminAPI.runReconciliation({ ...form, bank_balance: parseFloat(form.bank_balance) }); toast.success('Reconciliation completed'); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setRunning(false); }
  };

  const handleResolve = async (id) => {
    const notes = window.prompt('Enter resolution notes:');
    if (!notes) return;
    try { await superAdminAPI.resolveReconciliation(id, { resolution_notes: notes }); toast.success('Resolved'); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-6" data-testid="sa-reconciliation">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-gray-900">Reconciliation</h1><p className="text-sm text-gray-500">Daily bank vs ledger balance checks</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="run-recon-btn"><Plus className="w-4 h-4 mr-2" /> Run Check</Button></DialogTrigger>
          <DialogContent className="border-gray-200"><DialogHeader><DialogTitle>Run Reconciliation</DialogTitle></DialogHeader>
            <form onSubmit={handleRun} className="space-y-4">
              <div><Label className="text-sm">Date</Label><Input type="date" value={form.reconciliation_date} onChange={e => setForm({...form, reconciliation_date: e.target.value})} required className="h-9" /></div>
              <div><Label className="text-sm">Bank Balance (NGN)</Label><Input type="number" step="0.01" value={form.bank_balance} onChange={e => setForm({...form, bank_balance: e.target.value})} required className="h-9" /></div>
              <Button type="submit" disabled={running} className="w-full bg-pink-500 hover:bg-pink-600 text-white">{running ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Run Check'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-3">
        {records.length === 0 ? <Card className="border-gray-200"><CardContent className="p-8 text-center text-gray-400 text-sm">No reconciliation records yet</CardContent></Card>
        : records.map(r => (
          <Card key={r.id} className="border-gray-200 shadow-sm">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${r.is_balanced ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                  {r.is_balanced ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{r.reconciliation_date}</p>
                  <p className="text-xs text-gray-500">Bank: NGN {Number(r.bank_balance).toLocaleString()} | Ledger: NGN {Number(r.total_wallet_balances).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!r.is_balanced && r.status !== 'resolved' && <Button size="sm" variant="outline" onClick={() => handleResolve(r.id)} className="text-xs border-amber-200 text-amber-600 hover:bg-amber-50">Resolve</Button>}
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${r.status === 'balanced' ? 'bg-emerald-50 text-emerald-600' : r.status === 'resolved' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>{r.status}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
